<template>
  <div :class="wrapperClasses">
    <!-- Mascot on the left (for default variant) -->
    <div
      v-if="shouldShowMascots && computedMascotPosition === 'left'"
      :class="mascotContainerClasses"
    >
      <mascot-display
        :mascot-ids="['rävmond']"
        :layout="mascotConfig.layout"
        :size="mascotConfig.size"
        :show-height-difference="mascotConfig.showHeightDifference"
        :variant="mascotVariant"
        :direction="computedMascotPosition"
        class="pointer-events-none"
      />
    </div>

    <section :class="bannerClasses">
      <!-- Info section - order changes based on mascot position -->
      <div :class="infoClasses">
        <div :class="infoTextClasses">
          <h2 :class="titleClasses">
            {{ title }}
          </h2>
          <p class="py-2">{{ text }}</p>
          <app-button
            :class="actionClasses"
            variant="lila"
            size="desktop"
            type="generate"
            class="mt-4"
            @click="generateNewJoke"
          >
            {{ buttonText }}
          </app-button>
        </div>
      </div>
      <!-- Joke section - order changes based on mascot position -->
      <div :class="jokeClasses">
        <div class="relative flex h-full w-full items-center justify-center self-center">
          <div class="p-8 text-center">
            <div v-if="currentJokeData">
              <h2 :class="jokeQuestionClasses">{{ currentJokeData.question }}</h2>
              <p :class="jokeAnswerClasses">{{ currentJokeData.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Mascot on the right (for default variant) -->
    <div
      v-if="shouldShowMascots && computedMascotPosition === 'right'"
      :class="mascotContainerClasses"
    >
      <mascot-display
        :mascot-ids="['rävmond']"
        :layout="mascotConfig.layout"
        :size="mascotConfig.size"
        :show-height-difference="mascotConfig.showHeightDifference"
        :variant="mascotVariant"
        :direction="computedMascotPosition"
        class="pointer-events-none"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMascots } from '@/composables/useMascots'

/**
 * JokeBlock Component
 *
 * An interactive block that displays animal jokes for kids.
 * Features Rävmond mascot and generates random jokes on button click.
 *
 * Usage:
 * <joke-block />
 * <joke-block mascot-position="right" />
 *
 * @example
 * <!-- Default usage with mascot on the left -->
 * <joke-block />
 *
 * <!-- With mascot on the right -->
 * <joke-block mascot-position="right" />
 */

interface Props {
  mascotPosition?: 'left' | 'right'
}

const props = withDefaults(defineProps<Props>(), {
  mascotPosition: 'left',
})

// Mascot functionality
const { getMascots, getMascotConfig } = useMascots()

// Get mascot configuration for joke block
const mascotConfig = computed(() => {
  return getMascotConfig('jokeBlock')
})

// Get mascot from current joke
const computedMascotIds = computed((): string[] => {
  const currentJokeData = jokes[currentJokeIndex.value]
  return currentJokeData ? [currentJokeData.mascot] : ['rävmond']
})

// Mascot position
const computedMascotPosition = computed((): 'left' | 'right' => {
  return props.mascotPosition || 'right'
})

// Get mascots with validation
const mascots = computed(() => {
  return getMascots(computedMascotIds.value)
})

// Determine if we should show mascots
const shouldShowMascots = computed(() => {
  return mascots.value.length > 0
})

// Mascot variant - use happy for jokes
const mascotVariant = computed(() => {
  return 'happy' as const
})

// Hardcoded jokes array with mascots
const jokes = [
  {
    question: 'Vart satt de finaste fjädrarna någonstans?',
    answer: 'På-fågeln!',
    mascot: 'rävmond', // Chicken mascot for bird joke
  },
  {
    question: 'Hur förstod kossorna var de helst ville beta?',
    answer: 'De fattade på-ängen!',
    mascot: 'rävmond', // Cow mascot for cow joke
  },
  {
    question: 'Vet du varför tuppar är så smarta?',
    answer: 'De har högt kuckel-IQ',
    mascot: 'rävmond', // Chicken mascot for rooster joke
  },
  {
    question: 'Vad är alla fåglarnas favoritläsk?',
    answer: 'Kråka-kolan!',
    mascot: 'rävmond', // Chicken mascot for bird joke
  },
  {
    question: 'Vad är kossors favorithobby?',
    answer: 'Muuusik!',
    mascot: 'rävmond', // Cow mascot for cow joke
  },
  {
    question: 'Vad kallas grisar som gillar att gå upp tidigt?',
    answer: 'Morgon-pig!',
    mascot: 'rävmond', // Pig mascot for pig joke
  },
  {
    question: 'Hur låter en lockig hund som säger hejdå?',
    answer: 'Pudel-oo!',
    mascot: 'rävmond', // Fox mascot for dog joke (closest animal)
  },
  {
    question: 'Vad blir det när en katt funderar?',
    answer: 'Misstankar!',
    mascot: 'rävmond', // Fox mascot for cat joke (closest animal)
  },
  {
    question: 'Vilken fågel är mest social?',
    answer: 'Um-gås!',
    mascot: 'rävmond', // Chicken mascot for bird joke
  },
  {
    question: 'Vad kallas fårens ungar?',
    answer: 'Bääääbisar!',
    mascot: 'rävmond', // Cow mascot for sheep joke (closest farm animal)
  },
  {
    question: 'Vad kallas det när underjordiska djur håller hand?',
    answer: 'De håller masken!',
    mascot: 'rävmond', // Fox mascot for general animal joke
  },
  {
    question: 'Vad är kaniners favoritmusik?',
    answer: 'Hip-hopp!',
    mascot: 'rävmond', // Fox mascot for rabbit joke (closest animal)
  },
  {
    question: 'Vad heter det när elefanter ramlar?',
    answer: 'Snubel!',
    mascot: 'rävmond', // Fox mascot for elephant joke (closest animal)
  },
  {
    question: 'Vilket djur kommer aldrig behöva glasögon?',
    answer: 'En Ze-bra!',
    mascot: 'rävmond', // Fox mascot for zebra joke (closest animal)
  },
  {
    question: 'Vilket djur är bäst på att återvinna läskburkar?',
    answer: 'Pant-ern!',
    mascot: 'rävmond', // Fox mascot for panther joke (closest animal)
  },
  {
    question: 'Vilket djur älskar att ge bort presenter?',
    answer: 'Ge-ting!',
    mascot: 'rävmond', // Fox mascot for general animal joke
  },
  {
    question: 'Hur är människans bästa vän?',
    answer: 'Hunderbar!',
    mascot: 'rävmond', // Fox mascot for dog joke (closest animal)
  },
  {
    question: 'Vilket djur tycker mest om att dansa?',
    answer: 'Dis-kon!',
    mascot: 'rävmond', // Fox mascot for general animal joke
  },
]

// Reactive state
const currentJokeIndex = ref(0)

// Get current joke data
const currentJokeData = computed(() => {
  return jokes[currentJokeIndex.value]
})

// Static content
const title = 'Hej! Jag heter Rävmond och jag älskar att skoja!'
const text = 'Tryck på knappen så får du höra ett nytt skämt.'
const buttonText = 'Nytt skämt'

// Generate a new joke
const generateNewJoke = () => {
  let newIndex
  do {
    newIndex = Math.floor(Math.random() * jokes.length)
  } while (newIndex === currentJokeIndex.value && jokes.length > 1)

  currentJokeIndex.value = newIndex
}

// Initialize with first joke
onMounted(() => {
  generateNewJoke()
})

const bannerClasses = computed((): string => {
  const baseClasses = 'flex flex-col overflow-hidden w-full'

  // Border radius based on mascot placement
  let defaultRadiusClasses = ''
  if (shouldShowMascots.value) {
    if (computedMascotPosition.value === 'left') {
      defaultRadiusClasses = 'rounded-[20px] rounded-tl-[0px]'
    } else {
      defaultRadiusClasses = 'rounded-[20px] rounded-br-[0px]'
    }
  } else {
    defaultRadiusClasses = 'rounded-[20px]'
  }
  return `${baseClasses} ${defaultRadiusClasses} bg-white sm:flex-row ${shouldShowMascots.value ? 'flex-1' : ''}`
})

const jokeClasses = computed((): string => {
  const baseClasses = ' flex flex-1 basis-2/5 flex flex-col justify-center items-start'

  // Add order class based on mascot position
  const orderClass =
    shouldShowMascots.value && computedMascotPosition.value === 'left' ? 'order-2' : 'order-1'

  return `${baseClasses} bg-peach-lightest relative min-h-72 flex-1 basis-3/5 overflow-hidden pr-2 ${orderClass}`
})

const infoClasses = computed((): string => {
  const baseClasses = ' cta-banner-media sm:cta-banner-media'

  // Add order class based on mascot position
  const orderClass =
    shouldShowMascots.value && computedMascotPosition.value === 'left' ? 'order-1' : 'order-2'

  return `${baseClasses} text-center ${orderClass}`
})

const infoTextClasses = computed((): string => {
  return 'px-[23px] md:px-[70px] py-[21px] md:py-[74px] text-left font-body'
})

const titleClasses = computed((): string => {
  return 'font-headline text-h3 font-normal text-black text-left'
})

const jokeQuestionClasses = computed((): string => {
  return 'font-headline text-h2 font-normal text-black text-center mb-4 w-full'
})

const jokeAnswerClasses = computed((): string => {
  return 'font-body text-preamble font-bold text-slate-dark text-center w-full'
})

const actionClasses = computed((): string => {
  return 'w-full md:w-auto py-6 mt-2 py-1 px-6'
})

// Mascot container classes with justify-content
const mascotContainerClasses = computed((): string => {
  const baseClasses = 'pointer-events-none flex flex-shrink-0 items-center'
  const justifyClass =
    computedMascotPosition.value === 'left' ? 'justify-start' : 'justify-end self-end'
  return `${baseClasses} ${justifyClass}`
})

// Wrapper classes for flexbox layout
const wrapperClasses = computed((): string => {
  if (shouldShowMascots.value) {
    return 'flex items-start gap-4 sm:flex-row flex-col'
  }
  return ''
})
</script>
